import { Turnstile, turnstileSiteKey } from '@/components/auth/Turnstile';
import { useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import PageLayout from '@/components/layout/PageLayout';
import { toast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { SectionHeading } from '@/components/ui/section-heading';

const registerSchema = z.object({
  firstName: z.string().min(1, { message: 'First name is required' }),
  lastName: z.string().min(1, { message: 'Last name is required' }),
  email: z.string().email({ message: 'Please enter a valid email address' }),
  password: z.string()
    .min(8, { message: 'Password must be at least 8 characters' })
    .regex(/[A-Z]/, { message: 'Password must contain at least one capital letter' })
    .regex(/[0-9]/, { message: 'Password must contain at least one number' })
    .regex(/[^a-zA-Z0-9]/, { message: 'Password must contain at least one special character' }),
  confirmPassword: z.string(),
}).refine((data) => data.password === data.confirmPassword, {
  message: "Passwords don't match",
  path: ["confirmPassword"],
});

type RegisterFormValues = z.infer<typeof registerSchema>;

export default function Register() {
  const { user, signUp } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [captchaToken, setCaptchaToken] = useState('');
  const [captchaResetKey, setCaptchaResetKey] = useState(0);
  const navigate = useNavigate();

  const form = useForm<RegisterFormValues>({
    resolver: zodResolver(registerSchema),
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      password: '',
      confirmPassword: '',
    },
  });

  const checkUserExists = async (email: string): Promise<boolean> => {
    try {
      const { data, error } = await supabase.rpc('check_user_exists', { 
        p_email: email 
      });
      
      if (error) {
        console.error('Error checking if user exists:', error);
        return false; // Default to false if there's an error
      }
      
      // Ensure we're converting to boolean explicitly
      return data === true;
    } catch (error) {
      console.error('Exception checking if user exists:', error);
      return false;
    }
  };

  const onSubmit = async (data: RegisterFormValues) => {
    if (turnstileSiteKey && !captchaToken) return;
    setIsLoading(true);
    try {
      // First check if the user already exists
      const userExists = await checkUserExists(data.email);
      
      if (userExists) {
        // User already exists, show appropriate message and redirect
        toast({
          title: "Account already exists",
          description: "An account with this email already exists. Redirecting to login."
        });
        
        // Add a slight delay before redirecting to ensure toast is shown
        setTimeout(() => {
          navigate('/auth/login', { 
            state: { email: data.email, fromRegistration: true } 
          });
        }, 1500);
        
        return;
      }
      
      // User doesn't exist, proceed with registration
      await signUp(data.email, data.password, data.firstName, data.lastName, captchaToken);
      
      toast({
        title: "Registration successful!",
        description: 'Your account is pending approval.'
      });
      
    } catch (error: any) {
      console.error('Registration error:', error);
      
      // Handle other errors that might occur during registration
      toast({
        variant: "destructive",
        title: "Registration Error",
        description: error.message || 'Failed to register. Please try again.'
      });
    } finally {
      setCaptchaToken('');
      setCaptchaResetKey((key) => key + 1);
      setIsLoading(false);
    }
  };

  // Redirect if user is already logged in
  if (user) {
    return <Navigate to="/" replace />;
  }

  return (
    <PageLayout>
      <div className="container max-w-md mx-auto py-16">
        <div className="space-y-6">
          <div className="space-y-2 text-center">
            <SectionHeading
              title="Register"
              gradient={true}
            />
            <p className="text-gray-500">Create an account to get started</p>
          </div>

          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="firstName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>First Name</FormLabel>
                      <FormControl>
                        <Input placeholder="John" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="lastName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Last Name</FormLabel>
                      <FormControl>
                        <Input placeholder="Doe" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Email</FormLabel>
                    <FormControl>
                      <Input placeholder="email@example.com" type="email" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="text-sm text-gray-500 bg-gray-50 p-3 rounded-lg">
                <p>Password must contain:</p>
                <ul className="list-disc list-inside mt-1">
                  <li>At least 8 characters</li>
                  <li>At least one capital letter</li>
                  <li>At least one number</li>
                  <li>At least one special character</li>
                </ul>
              </div>

              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Password</FormLabel>
                    <FormControl>
                      <Input placeholder="••••••••" type="password" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="confirmPassword"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Confirm Password</FormLabel>
                    <FormControl>
                      <Input placeholder="••••••••" type="password" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <Turnstile onToken={setCaptchaToken} resetKey={captchaResetKey} />

              <Button type="submit" className="w-full" disabled={isLoading || Boolean(turnstileSiteKey && !captchaToken)}>
                {isLoading ? 'Registering...' : 'Register'}
              </Button>
            </form>
          </Form>

          <div className="text-center">
            <p className="text-sm text-gray-500">
              Already have an account?{' '}
              <Link to="/auth/login" className="text-taekwondo-blue hover:underline">
                Login
              </Link>
            </p>
          </div>
        </div>
      </div>
    </PageLayout>
  );
}
